// src/components/ProgressChart.js
import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

/**
 * ProgressChart - Gráfica de líneas reutilizable para el módulo de progreso.
 *
 * Props:
 *  - logs: Array de objetos ProgressLog del backend
 *  - metric: string - clave del campo a graficar ('weight_kg', 'waist_cm', etc.)
 *  - label: string - etiqueta legible para la métrica (ej. 'Peso (kg)')
 */
const METRIC_COLORS = {
  weight_kg: 'rgb(239, 68, 68)',
  body_fat_percentage: 'rgb(249, 115, 22)',
  waist_cm: 'rgb(234, 179, 8)',
  chest_cm: 'rgb(34, 197, 94)',
  hips_cm: 'rgb(59, 130, 246)',
  bicep_cm: 'rgb(168, 85, 247)',
  thigh_cm: 'rgb(236, 72, 153)',
};

const ProgressChart = ({ logs = [], metric = 'weight_kg', label = 'Peso (kg)' }) => {
  const filteredLogs = logs.filter((log) => log[metric] != null);

  if (filteredLogs.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 bg-black bg-opacity-20 rounded-lg border border-dashed border-gray-600">
        <p className="text-gray-500 text-sm">Sin datos suficientes para mostrar la gráfica.</p>
      </div>
    );
  }

  const color = METRIC_COLORS[metric] || 'rgb(239, 68, 68)';

  const data = {
    labels: filteredLogs.map((log) =>
      new Date(log.date + 'T00:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })
    ),
    datasets: [
      {
        label,
        data: filteredLogs.map((log) => log[metric]),
        borderColor: color,
        backgroundColor: color.replace('rgb', 'rgba').replace(')', ', 0.1)'),
        tension: 0.3,
        pointBackgroundColor: color,
        pointRadius: 5,
        pointHoverRadius: 7,
        fill: true,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        titleColor: '#f9fafb',
        bodyColor: '#d1d5db',
        borderColor: 'rgba(75, 85, 99, 0.5)',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        ticks: { color: '#9ca3af', font: { size: 11 } },
        grid: { color: 'rgba(75, 85, 99, 0.2)' },
      },
      y: {
        ticks: { color: '#9ca3af', font: { size: 11 } },
        grid: { color: 'rgba(75, 85, 99, 0.2)' },
      },
    },
  };

  return <Line data={data} options={options} />;
};

export default ProgressChart;
